/**
 * auto-sync orchestration — Family (CLAUDE.md §6 madde 11–12)
 *
 * Wires browser-level events to the data layer:
 *   - `online`  — drain the outbox (queued sends),
 *   - `offline` — no-op (the queue would skip itself anyway, but we log so
 *                 the UI can surface a banner via `useOnlineStatus`),
 *   - `visibilitychange` (visible) — re-drain in case messages piled up while
 *                 the tab was in the background and the OS suspended timers,
 *   - app startup — single `kickstart()` call from `main.tsx` after auth.
 *
 * The module installs ONE shared listener set per app process. Calling
 * `installAutoSync()` multiple times is safe — subsequent calls return the
 * existing teardown.
 *
 * Initial-data sync (conversations, users) is intentionally NOT triggered
 * here. That is owned by feature modules (e.g. the contacts hook calls
 * `syncUsersFromServer` on mount via TanStack Query). Auto-sync is only
 * about the writes-pending queue, where there is no React tree to host a
 * query.
 */

import { processQueue } from './queue';

let installed = false;

/**
 * Install the auto-sync listeners. Returns a teardown for tests / HMR.
 *
 * Behavior:
 *   - on `online`           → kick `processQueue` (best-effort, no await)
 *   - on `visibilitychange` (becoming visible AND online) → same
 *   - calls processQueue once immediately (kickstart on mount)
 */
export function installAutoSync(): () => void {
  if (typeof window === 'undefined') {
    return () => {
      /* no-op */
    };
  }
  if (installed) {
    // Already installed → return a no-op teardown so callers don't blow up.
    return () => {
      /* no-op */
    };
  }
  installed = true;

  const drain = () => {
    void processQueue().catch(() => {
      // Swallow — `processQueue` already records lastError per row. We don't
      // want a console spam loop on hour-long offline stretches.
    });
  };

  const onOnline = () => {
    drain();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      drain();
    }
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);

  // Kick once on install so a fresh load with queued items from a previous
  // session doesn't wait for the next event.
  if (navigator.onLine) {
    drain();
  }

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
    installed = false;
  };
}
